/**
 * Conditional text (D55) — one authored string that knows what the world is
 * doing.
 *
 * @remarks
 * `openable.openDescription` / `closedDescription` (D50, retired by D159's
 * migration into the variants below) turned out to be a hard-coded special
 * case of something more general. A thing with a door reads differently once
 * the door has moved (D50), and so does a thing with a switch, and a thing
 * behind a flag, and a thing you are holding — and the moment a thing has TWO
 * such properties, per-state description fields stop working: a cabinet that
 * is open-or-shut AND running-or-dead has four readings and one description
 * slot. Precedence loses three of them; concatenation writes prose by machine.
 * Neither is acceptable, and both are working around the wrong primitive.
 *
 * So the primitive is this: a description is either a plain string — the
 * overwhelmingly common case, unchanged and unmigrated — or a set of
 * CONDITIONAL VARIANTS with a mandatory fallback.
 *
 * ```jsonc
 * "description": {
 *   "fallback": "Shut, and silent.",
 *   "variants": [
 *     { "condition": { "conditions": [doorOpen, running], "type": "all_of" }, "text": "Open, and still breathing cold at you." },
 *     { "condition": doorOpen, "text": "Open, and the light inside is dead." },
 *     { "condition": running,  "text": "Shut, humming." }
 *   ]
 * }
 * ```
 *
 * **Why an object with a named `fallback` rather than a flat list whose last
 * entry has no condition.** The flat form is the more familiar idiom, and it
 * can express two mistakes this cannot: a list with no unconditional entry (a
 * describable thing with nothing to say when every variant misses) and text
 * placed AFTER the unconditional entry (authored content nothing can ever
 * reach). Both would need a validator to catch. Here they are unrepresentable,
 * which engineering_standards §2.1 prefers and which costs one extra key.
 *
 * `variants` is ordered and FIRST MATCH WINS, so the most specific reading is
 * authored first — exactly how a reader expects a list of cases to behave.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { nonBlankStringSchema } from './primitives.js';

/**
 * One conditional reading: this text, whenever this condition holds.
 *
 * @remarks
 * The condition is REQUIRED here — an unconditional variant would be a second
 * fallback, silently shadowing the real one and stranding everything after it.
 * The one unconditional reading lives in {@link conditionalTextSchema}'s
 * `fallback`, where it cannot be shadowed and cannot be forgotten.
 */
export const conditionalTextVariantSchema = z
	.strictObject({
		condition: conditionSchema,
		text: nonBlankStringSchema,
	})
	.readonly();

/** One conditional reading — see {@link conditionalTextVariantSchema}. */
export type ConditionalTextVariant = z.infer<typeof conditionalTextVariantSchema>;

/**
 * Authored text that may vary with world state — see the module remarks.
 *
 * @remarks
 * The plain-string arm is not a legacy shim. Most things look the same
 * whatever the world is doing, and making every author write `{ "fallback":
 * "…" }` to say so would be ceremony charged to the common case.
 *
 * `variants` is `min(1)` because the object form with no variants is a string
 * with extra steps, and saying the same thing two ways is the duplication
 * §2.1 refuses.
 */
export const conditionalTextSchema = z.union([
	nonBlankStringSchema,
	z
		.strictObject({
			fallback: nonBlankStringSchema,
			variants: z.array(conditionalTextVariantSchema).min(1).readonly(),
		})
		.readonly(),
]);

/** Authored text that may vary with world state — see {@link conditionalTextSchema}. */
export type ConditionalText = z.infer<typeof conditionalTextSchema>;

/**
 * Every string a conditional text can speak: the text itself, or the
 * fallback and then every variant, in declared order.
 *
 * @remarks
 * The static reader, for a walker that has no world to resolve against — the
 * prose-promise audit (D61) reading what a room could ever say, the engine
 * indexing the words an item's page uses (D205). The engine's
 * `resolveConditionalText` is the other reader, and it picks ONE.
 *
 * @param text - The authored text
 * @returns Every reading
 */
export const readingsOf = (text: ConditionalText): readonly string[] =>
	typeof text === 'string' ? [text] : [text.fallback, ...text.variants.map((variant) => variant.text)];

/**
 * D140 — one sentence that is true IN ADDITION to a description whenever
 * `when` holds: appended, in declared order, after whichever base form
 * resolved. A variant says "this text INSTEAD"; a fragment says "and also
 * this" — two authoring facts, two shapes, and neither can express the
 * other (Calder's ruling, 2026-09-03: a fragment that could REPLACE would be
 * a variant with extra steps, and replace has no honest way to name what it
 * replaces). The thread of red light under the substation's door and the gas
 * reaching a tunnel room are both this; before it, each was a whole second
 * copy of the description with one sentence changed, maintained twice.
 */
export const descriptionFragmentSchema = z
	.strictObject({
		text: conditionalTextSchema,
		when: conditionSchema,
	})
	.readonly();

/** One appended sentence under a condition — see {@link descriptionFragmentSchema}. */
export type DescriptionFragment = z.infer<typeof descriptionFragmentSchema>;

/**
 * D237 — the two forms a room is read in (D82): `full` on first entry and on
 * `look`, `brief` on every re-entry. The describer's own parameter, named
 * once so the schema and the engine cannot disagree about what a form is.
 */
export const roomDescriptionFormSchema = z.enum(['brief', 'full']);

/** A form a room is read in — see {@link roomDescriptionFormSchema}. */
export type RoomDescriptionForm = z.infer<typeof roomDescriptionFormSchema>;

/**
 * D237 — a room's fragment, which may belong to ONE of the room's two forms.
 *
 * @remarks
 * A fragment is appended to whichever form was read (D140), because "the gas
 * is here" is true of a room however briefly it is described. Some sentences
 * are not like that: they finish the PAGE, and the re-orientation never said
 * them. PATHOLOGY 2's air is one — its full page ends on the sweetness, its
 * brief form names four tables and a door — and before this the only way to
 * make that sentence change was a variant repeating the five sentences before
 * it (D236's accepted warning). `form` says which reading the sentence is part
 * of; absent, it is part of both, as every fragment was.
 *
 * Rooms only: an item's or an actor's page has one form, so the field would
 * mean nothing there, and a field that means nothing is a field nobody can
 * set correctly.
 */
export const roomDescriptionFragmentSchema = z
	.strictObject({
		...descriptionFragmentSchema.unwrap().shape,
		form: roomDescriptionFormSchema.optional(),
	})
	.readonly();

/** One appended sentence under a condition, in one form or both — see {@link roomDescriptionFragmentSchema}. */
export type RoomDescriptionFragment = z.infer<typeof roomDescriptionFragmentSchema>;
